import React, { useState, useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Divider, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Wrench,
  Brain,
  LineChart, 
  Lightbulb,
  ArrowLeft
} from 'lucide-react';
import HolographicStepper from '../common/HolographicStepper.tsx';
import ProblemSolvingToolbox from '../../pages/Home/components/ProblemSolvingToolbox.tsx';

export interface ProblemSolvingToolboxProps {
  problemStatement: string;
  onCostChange: (cost: number) => void;
  currentCost: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: '#00FFFF',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#00FFFF',
  },
  '&:hover': {
    color: '#00FFFF',
    opacity: 0.8,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  height: '100%',
  overflowY: 'auto',
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`toolbox-tabpanel-${index}`}
      aria-labelledby={`toolbox-tab-${index}`}
      {...other}
      style={{ height: 'calc(100% - 49px)' }}
    >
      {value === index && (
        <Box sx={{ height: '100%', overflowY: 'auto', pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const ProblemSolvingToolboxContent: React.FC<ProblemSolvingToolboxProps> = ({
  problemStatement,
  onCostChange,
  currentCost
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [frameworkCost, setFrameworkCost] = useState(0);
  const [mindsetCost, setMindsetCost] = useState(0);
  const [analyticsCost, setAnalyticsCost] = useState(0);
  const [innovationCost, setInnovationCost] = useState(0);
  
  // Add state for snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFrameworkCostChange = useCallback((cost: number) => {
    setFrameworkCost(cost);
  }, []);

  const handleMindsetCostChange = useCallback((cost: number) => {
    setMindsetCost(cost);
  }, []);

  const handleAnalyticsCostChange = useCallback((cost: number) => {
    setAnalyticsCost(cost);
  }, []);

  const handleInnovationCostChange = useCallback((cost: number) => {
    setInnovationCost(cost);
  }, []);

  // Add handler for proposal addition
  const handleAddToProposal = (toolboxType: string, cost: number) => {
    // In a real application, this would add the selected tools to a proposal in your state management
    console.log(`Adding ${toolboxType} tools to proposal with cost $${cost}`);
    
    // Show success message
    setSnackbarMessage(`Added ${toolboxType} tools to your proposal (Cost: $${cost.toLocaleString()})`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    // Here you would also potentially reset the selections or navigate to a proposal review page
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const frameworkSteps = [
    {
      label: 'Select Frameworks',
      content: <ProblemSolvingToolbox onCostChange={handleFrameworkCostChange} currentCost={frameworkCost} />,
      icon: <Wrench size={16} />,
      cost: frameworkCost
    },
    {
      label: 'Apply to Problem',
      content: (
        <SectionContainer>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Apply Frameworks to Your Problem
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Use the selected frameworks to analyze and break down your problem into manageable components.
          </Typography>
        </SectionContainer>
      ),
      icon: <ArrowLeft size={16} />,
      cost: 0
    },
    {
      label: 'Document Insights',
      content: (
        <SectionContainer>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Document Your Insights
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Record the key insights gained from applying the frameworks to your problem.
          </Typography>
        </SectionContainer>
      ),
      icon: <LineChart size={16} />,
      cost: 0
    }
  ];

  const mindsetSteps = [
    {
      label: 'Choose Mindsets',
      content: <ProblemSolvingToolbox onCostChange={handleMindsetCostChange} currentCost={mindsetCost} />,
      icon: <Brain size={16} />,
      cost: mindsetCost
    },
    {
      label: 'Mental Reframing',
      content: (
        <SectionContainer>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Reframe Your Problem
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Apply the selected mindsets to view your problem from different perspectives.
          </Typography>
        </SectionContainer>
      ),
      icon: <Lightbulb size={16} />,
      cost: 0
    }
  ];

  const analyticsSteps = [
    {
      label: 'Select Analytics Tools',
      content: <ProblemSolvingToolbox onCostChange={handleAnalyticsCostChange} currentCost={analyticsCost} />,
      icon: <LineChart size={16} />,
      cost: analyticsCost
    },
    {
      label: 'Gather Data',
      content: (
        <SectionContainer>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Collect Relevant Data
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Identify and gather the data needed to analyze your problem effectively.
          </Typography>
        </SectionContainer>
      ),
      icon: <LineChart size={16} />,
      cost: 0
    }
  ];

  const innovationSteps = [
    {
      label: 'Choose Innovation Methods',
      content: <ProblemSolvingToolbox onCostChange={handleInnovationCostChange} currentCost={innovationCost} />,
      icon: <Lightbulb size={16} />,
      cost: innovationCost
    },
    {
      label: 'Generate Ideas',
      content: (
        <SectionContainer>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Brainstorm Solutions
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Use creative techniques to generate potential solutions to your problem.
          </Typography>
        </SectionContainer>
      ),
      icon: <Lightbulb size={16} />,
      cost: 0
    }
  ];

  return (
    <ContentContainer>
      <Typography variant="h4" sx={{ color: '#fff', mb: 3 }}>
        Problem Solving Toolbox
      </Typography>
      
      <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 4 }}>
        Access a variety of problem-solving methodologies, frameworks, and tools to help you analyze and solve complex problems effectively.
      </Typography>
      
      <Divider sx={{ mb: 3, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
      
      <StyledTabs 
        value={tabValue} 
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="inherit"
      >
        <StyledTab icon={<Wrench size={16} />} label="Frameworks" />
        <StyledTab icon={<Brain size={16} />} label="Mindsets" />
        <StyledTab icon={<LineChart size={16} />} label="Analytics" />
        <StyledTab icon={<Lightbulb size={16} />} label="Innovation" />
      </StyledTabs>
      
      <TabPanel value={tabValue} index={0}>
        <HolographicStepper 
          steps={frameworkSteps}
          orientation="vertical"
          showContent={true}
          onSubmitRFP={() => handleAddToProposal('Framework', frameworkCost)}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <HolographicStepper 
          steps={mindsetSteps}
          orientation="vertical"
          showContent={true}
          onSubmitRFP={() => handleAddToProposal('Mindset', mindsetCost)}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={2}>
        <HolographicStepper 
          steps={analyticsSteps}
          orientation="vertical"
          showContent={true}
          onSubmitRFP={() => handleAddToProposal('Analytics', analyticsCost)}
        />
      </TabPanel>
      
      <TabPanel value={tabValue} index={3}>
        <HolographicStepper 
          steps={innovationSteps}
          orientation="vertical"
          showContent={true}
          onSubmitRFP={() => handleAddToProposal('Innovation', innovationCost)}
        />
      </TabPanel>
      
      {/* Add snackbar for feedback */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%',
            background: snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.2)' : 'rgba(244, 67, 54, 0.2)',
            color: snackbarSeverity === 'success' ? '#00CC99' : '#f44336',
            border: `1px solid ${snackbarSeverity === 'success' ? 'rgba(0, 204, 153, 0.5)' : 'rgba(244, 67, 54, 0.5)'}`,
            backdropFilter: 'blur(10px)',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ContentContainer>
  );
};

export default ProblemSolvingToolboxContent; 